import React, {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {useSelector} from "react-redux";
import moment from "moment";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import CardsContainer from "@/components/CardsContainer";
import CardsList from "@/components/CardsList";
import CardsTable from "@/components/CardsTable";

import {PlayerValuationAPI} from "@/api/player_valuation";

import AddIcon from "@mui/icons-material/Add";

import {dateFormat} from "@/utils/constants";
import {getAlertSeverity} from "@/utils/functions";

import "./index.scss";
import {useTranslation} from "react-i18next";

const fieldDateFormat = "YYYY-MM-DD";

const defaultFiltersData = {
	toDate: moment().endOf("month").format(fieldDateFormat),
	tryouts: true,
};

function TryoutsPage(props) {
	const {expanded = true, playerId} = props;

	const navigate = useNavigate();
	const seasonId = useSelector((state) => state?.season?.id);
	const {t} = useTranslation();

	const [loading, setLoading] = useState(false);
	const [valuations, setValuations] = useState([]);

	const [filtersData, setFiltersData] = useState(
		JSON.parse(sessionStorage.getItem("tryouts_filters")) || defaultFiltersData,
	);

	useEffect(() => {
		if (filtersData) {
			const updatedFilters = {...filtersData, tryouts: true};
			sessionStorage.setItem("tryouts_filters", JSON.stringify(updatedFilters));
		}
	}, [filtersData]);

	const filters = [
		{
			key: "fromDate",
			type: "date",
			label: "Da",
			customClassName: "col-6 col-sm-4",
			clearable: false,
			value: filtersData?.fromDate
				? moment(filtersData?.fromDate).format(fieldDateFormat)
				: undefined,
			setValue: (e) => {
				if (e) {
					setFiltersData({
						...filtersData,
						fromDate: moment(e).format(fieldDateFormat),
					});
				}
			},
		},
		{
			key: "toDate",
			type: "date",
			label: "A",
			customClassName: "col-6 col-sm-4",
			clearable: false,
			value: moment(filtersData?.toDate).format(fieldDateFormat),
			setValue: (e) => {
				if (e) {
					setFiltersData({
						...filtersData,
						toDate: moment(e).format(fieldDateFormat),
					});
				}
			},
		},
	];

	const getValuations = async () => {
		setLoading(true);
		try {
			const response = playerId
				? await PlayerValuationAPI.playerList(playerId, {seasonId})
				: await PlayerValuationAPI.list({...filtersData, seasonId});
			setValuations(response);
		} catch (error) {
			console.log(error);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		getValuations();
	}, [filtersData, playerId]);

	const mainColumns = [
		{
			label: t("name"),
			key: "player.firstName",
			align: "start",
			sortable: true,
		},
		{
			label: "Cognome",
			key: "player.lastName",
			align: "start",
			sortable: true,
			sortedByDefault: true,
			sortedOrderByDefault: "ascend",
		},
		{
			label: "Data di nascita",
			key: "player.dateOfBirth",
			type: "date",
			customRender: (item) => {
				return item?.player?.dateOfBirth
					? moment(item?.player?.dateOfBirth).format(dateFormat)
					: "---";
			},
			medium: true,
			sortable: true,
		},
		{
			label: "Ruolo",
			key: "role",
			customRender: (item) => {
				return item?.player?.goalkeeper ? "P" : item?.player?.primaryRole?.[0];
			},
			small: true,
			align: "start",
		},
		{
			label: "N° valutazioni",
			key: "total",
			customRender: (item) => {
				return item?.children?.length;
			},
			align: "end",
			small: true,
		},
	];

	const columns = [
		{
			label: "Data",
			key: "valuationDate",
			type: "date",
			customRender: (item, rows) => {
				return moment(item.valuationDate).format(dateFormat);
			},
			small: true,
			sortable: true,
			sortedByDefault: true,
			sortedOrderByDefault: "descend",
		},
		{
			label: "Allenatore",
			key: "valuationAuthor",
			align: "start",
			medium: true,
		},

		{
			label: "Squadra",
			key: "club",
			customRender: (item) => {
				return item.club?.name;
			},
			medium: true,
			align: "start",
		},
		{
			label: "Categoria",
			key: "category",
			customRender: (item) => {
				return item.category?.name;
			},
			small: true,
			align: "start",
		},
		{
			label: "Esito",
			key: "finalOverallRating",
			customRender: (item) => {
				const alertData = getAlertSeverity(item.finalOverallRating, false) || {
					severity: "",
					message: "",
				};
				return (
					<div className={"severity " + (alertData.severity || "")}>
						{alertData.message || "---"}
					</div>
				);
			},
			small: true,
		},
		{
			label: "Voto finale",
			key: "finalAverage",
			customRender: (item) => {
				return +parseFloat(item.finalAverage).toFixed(2);
			},
			align: "end",
			small: true,
		},
	];

	// raggruppamento delle valutazioni per id del giocatore
	const handleReports = (data) => {
		const newReports = [];
		data.forEach((r) => {
			if (newReports.filter((nr) => nr.id === r.playerId)?.length) {
				newReports.map((nr) => {
					if (nr.id === r.playerId) {
						nr.children.push(r);
					}
					return nr;
				});
			} else {
				newReports.push({
					id: r.playerId,
					player: r.player,
					children: [r],
				});
			}
		});

		return newReports;
	};

	const listConfiguration = {
		data: valuations,
		onClick: (valuation) => navigate("/scouting/tryouts/" + valuation.id),
	};

	const mainActions = [
		{
			icon: <AddIcon />,
			onClick: () => navigate("/scouting/tryouts/new"),
		},
	];

	const pageList = (
		<CardsList
			type="valuation"
			breadcrumbs={!playerId}
			tabbed={playerId}
			fitHeight
			filtersData={!playerId ? filters : undefined}
			listConfiguration={listConfiguration}
			sortOptions={columns.filter((c) => c.sortable)}
		/>
	);

	const getChildRows = (parentRow) => {
		return parentRow.children || [];
	};

	const pageBody = (
		<CardsContainer
			tableComponent={
				<CardsTable
					fixExpanded={true}
					configurationColumns={expanded ? mainColumns : columns}
					configurationExpandedColumns={expanded ? columns : undefined}
					sortOptions={!playerId && mainColumns.filter((c) => c.sortable)}
					data={expanded ? handleReports(valuations) : valuations}
					breadcrumbs={!playerId}
					fullWidth
					fullHeight={!playerId}
					filtersData={!playerId ? filters : undefined}
					handleClick={(valuation) => {
						navigate("/scouting/tryouts/" + valuation.id);
					}}
					defaultSortedField="valuationDate"
					defaultSortedOrder="descend"
					rowGroupingKey={expanded ? "id" : undefined}
					getChildRows={expanded ? getChildRows : undefined}
				/>
			}
			listComponent={pageList}
		/>
	);

	if (playerId) {
		return pageBody;
	}

	return (
		<Page>
			<Loading visible={loading} />
			<Breadcrumbs actions={<MainActions actions={mainActions} />} />
			{pageBody}
		</Page>
	);
}

export default TryoutsPage;
